import { useCallback, useEffect, useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiError, toISODate } from "@/lib/api";
import { downloadFile } from "@/lib/utils2";
import { useAuth } from "@/context/AuthContext";
import { CashEntryForm } from "@/components/CashEntryForm";
import { CashLine, CashTotals } from "@/components/CashSheet";
import { CashReport } from "@/components/CashReport";

function DaySheet() {
  const { user } = useAuth();
  const [date, setDate] = useState(toISODate(new Date()));
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cash", { params: { start: date, end: date } });
      setEntries(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const remove = async (e) => {
    if (!window.confirm(`Eliminare il movimento di ${e.amount} € delle ${e.time}?`)) return;
    try {
      await api.delete(`/cash/${e.id}`);
      setEntries((l) => l.filter((x) => x.id !== e.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pdf = async () => {
    setBusy(true);
    try { await downloadFile("/cash/pdf", { start: date, end: date }, `incassi_${date}.pdf`); } catch { toast.error("Errore nella generazione del PDF"); }
    setBusy(false);
  };

  const by = (k) => entries.filter((e) => e.kind === k);
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="w-48 h-11" data-testid="cash-date-input" />
        <button onClick={pdf} disabled={busy} className="btn-secondary h-11 px-4" data-testid="cash-day-pdf-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />} PDF del giorno
        </button>
      </div>
      <CashEntryForm date={date} onAdded={(e) => setEntries((l) => [...l, e].sort((a, b) => a.time.localeCompare(b.time)))} />
      <div className="grid lg:grid-cols-[1fr_340px] gap-5 items-start">
        <div className="space-y-4">
          <CashLine title="Contanti" entries={by("contanti")} tone="bg-emerald-50 border-emerald-200 text-emerald-800" user={user} onDelete={remove} testid="cash-line-contanti" />
          <CashLine title="POS" entries={by("pos")} tone="bg-sky-50 border-sky-200 text-sky-800" user={user} onDelete={remove} testid="cash-line-pos" />
          <CashLine title="Spese del negozio" entries={by("spesa")} tone="bg-red-50 border-red-200 text-red-700" user={user} onDelete={remove} testid="cash-line-spese" />
          <p className="text-xs text-[var(--muted)]">Passa il mouse (o tocca) un importo per vedere ora e nota.</p>
        </div>
        <div className="lg:sticky lg:top-6"><CashTotals entries={entries} label={date === toISODate(new Date()) ? "Totale incassato oggi" : "Totale incassato"} /></div>
      </div>
    </div>
  );
}

export default function Cash() {
  return (
    <div className="space-y-6">
      <div className="fade-up">
        <p className="eyebrow">Cassa</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Incassi</h1>
      </div>
      <Tabs defaultValue="giornata">
        <TabsList className="bg-[var(--surface-2)] h-11 p-1">
          <TabsTrigger value="giornata" className="h-9" data-testid="cash-tab-giornata">Giornata</TabsTrigger>
          <TabsTrigger value="riepilogo" className="h-9" data-testid="cash-tab-riepilogo">Riepilogo e PDF</TabsTrigger>
        </TabsList>
        <TabsContent value="giornata" className="mt-6"><DaySheet /></TabsContent>
        <TabsContent value="riepilogo" className="mt-6"><CashReport /></TabsContent>
      </Tabs>
    </div>
  );
}
